import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Loader, CheckCircle2, XCircle, AlertCircle, Shield } from 'lucide-react';
import { executeCaptcha, loadRecaptchaScript } from '../utils/captcha';
import { buildApiUrl } from '../config/apiConfig';

const Signup = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    full_name: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [captchaReady, setCaptchaReady] = useState(false);
  
  // Validation states
  const [usernameCheck, setUsernameCheck] = useState({ checking: false, available: null, message: '' });
  const [emailValid, setEmailValid] = useState({ valid: null, message: '' });
  const [passwordStrength, setPasswordStrength] = useState({ strength: '', color: '', message: '' });
  const [errors, setErrors] = useState({});

  // Load reCAPTCHA on mount
  useEffect(() => {
    loadRecaptchaScript()
      .then(() => {
        setCaptchaReady(true);
        console.log('✅ reCAPTCHA loaded on Signup');
      })
      .catch((error) => {
        console.error('❌ reCAPTCHA load failed:', error);
        setCaptchaReady(true); // Continue without CAPTCHA
      });
  }, []);

  // Debounce timer for username check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.username.length >= 3) {
        checkUsernameAvailability(formData.username);
      } else if (formData.username.length > 0) {
        setUsernameCheck({ checking: false, available: false, message: 'Username too short (min 3 characters)' });
      } else {
        setUsernameCheck({ checking: false, available: null, message: '' });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.username]);

  // Real-time email validation
  useEffect(() => {
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValid = emailRegex.test(formData.email);
      
      if (isValid) {
        setEmailValid({ valid: true, message: 'Valid email format' });
      } else {
        setEmailValid({ valid: false, message: 'Invalid email format' });
      }
    } else {
      setEmailValid({ valid: null, message: '' });
    }
  }, [formData.email]);

  // Real-time password strength
  useEffect(() => {
    if (formData.password) {
      const strength = calculatePasswordStrength(formData.password);
      setPasswordStrength(strength);
    } else {
      setPasswordStrength({ strength: '', color: '', message: '' });
    }
  }, [formData.password]);

  const checkUsernameAvailability = async (username) => {
    setUsernameCheck({ checking: true, available: null, message: 'Checking...' });
    
    try {
      const response = await fetch(buildApiUrl(`/auth/check-username/${username}`));
      const data = await response.json();
      
      setUsernameCheck({
        checking: false,
        available: data.available,
        message: data.message
      });
    } catch (error) {
      setUsernameCheck({ checking: false, available: null, message: '' });
    }
  };

  const calculatePasswordStrength = (password) => {
    let score = 0;
    let feedback = [];

    // Length check
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    
    // Complexity checks
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    // Generate feedback
    if (password.length < 8) feedback.push('at least 8 characters');
    if (!/[A-Z]/.test(password)) feedback.push('uppercase letter');
    if (!/[a-z]/.test(password)) feedback.push('lowercase letter');
    if (!/[0-9]/.test(password)) feedback.push('number');

    // Determine strength
    if (score <= 2) {
      return {
        strength: 'Weak',
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        barColor: 'bg-red-500',
        width: '33%',
        message: feedback.length > 0 ? `Add ${feedback.join(', ')}` : 'Weak password'
      };
    } else if (score <= 4) {
      return {
        strength: 'Medium',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
        barColor: 'bg-yellow-500',
        width: '66%',
        message: feedback.length > 0 ? `Add ${feedback.join(', ')}` : 'Medium strength'
      };
    } else {
      return {
        strength: 'Strong',
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        barColor: 'bg-green-500',
        width: '100%',
        message: 'Excellent password!'
      };
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear field-specific error
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate before submit
    const newErrors = {};
    
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }
    
    if (!emailValid.valid) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!usernameCheck.available) {
      newErrors.username = usernameCheck.message || 'Username not available';
    }
    
    if (passwordStrength.strength === 'Weak') {
      newErrors.password = 'Password is too weak';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    
    try {
      // Execute reCAPTCHA before signup
      let captchaToken = null;
      if (captchaReady) {
        try {
          captchaToken = await executeCaptcha('signup');
          console.log('✅ reCAPTCHA token obtained for signup');
        } catch (captchaError) {
          console.warn('⚠️ reCAPTCHA failed, proceeding without it:', captchaError);
        }
      }
      
      // AuthContext signup auto-logs in and returns user data
      const userData = await signup(
        formData.email,
        formData.password,
        formData.username,
        formData.full_name,
        captchaToken
      );
      
      toast.success('Account created successfully!');
      
      // Redirect to onboarding or home
      navigate('/onboarding');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Signup failed. Please try again.';
      
      // Handle specific errors
      if (errorMessage.includes('email')) {
        setErrors({ email: 'Email already in use' });
        toast.error('Email already in use');
      } else if (errorMessage.includes('username')) {
        setErrors({ username: 'Username already taken' });
        toast.error('Username already taken');
      } else if (errorMessage.includes('captcha') || errorMessage.includes('bot')) {
        toast.error('Security check failed. Please try again.');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              WeboAsk
            </h1>
          </Link>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Join WeboAsk & start asking smarter questions
          </h2>
          <p className="text-gray-600">
            Get answers from experts and share your knowledge
          </p>
        </div>

        {/* Signup Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                  errors.full_name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="John Doe"
                disabled={loading}
              />
              {errors.full_name && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <XCircle className="w-4 h-4 mr-1" />
                  {errors.full_name}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                  errors.email ? 'border-red-300 bg-red-50' : 
                  emailValid.valid === true ? 'border-green-300' : 
                  emailValid.valid === false ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="you@example.com"
                disabled={loading}
              />
              {emailValid.message && (
                <p className={`text-sm mt-1 flex items-center ${
                  emailValid.valid ? 'text-green-600' : 'text-red-600'
                }`}>
                  {emailValid.valid ? (
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                  ) : (
                    <XCircle className="w-4 h-4 mr-1" />
                  )}
                  {emailValid.message}
                </p>
              )}
              {errors.email && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <XCircle className="w-4 h-4 mr-1" />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                  errors.username ? 'border-red-300 bg-red-50' : 
                  usernameCheck.available === true ? 'border-green-300' : 
                  usernameCheck.available === false ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="johndoe"
                disabled={loading}
              />
              {usernameCheck.checking && (
                <p className="text-gray-600 text-sm mt-1 flex items-center">
                  <Loader className="w-4 h-4 mr-1 animate-spin" />
                  Checking availability...
                </p>
              )}
              {!usernameCheck.checking && usernameCheck.message && (
                <p className={`text-sm mt-1 flex items-center ${
                  usernameCheck.available ? 'text-green-600' : 'text-red-600'
                }`}>
                  {usernameCheck.available ? (
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                  ) : (
                    <XCircle className="w-4 h-4 mr-1" />
                  )}
                  {usernameCheck.message}
                </p>
              )}
              {usernameCheck.available && (
                <p className="text-gray-500 text-xs mt-1">
                  Profile: weboask.com/u/{formData.username}
                </p>
              )}
              {errors.username && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <XCircle className="w-4 h-4 mr-1" />
                  {errors.username}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                    errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="••••••••"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {formData.password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-medium ${passwordStrength.color}`}>
                      {passwordStrength.strength}
                    </span>
                    <span className="text-xs text-gray-500">
                      {passwordStrength.message}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${passwordStrength.barColor} transition-all duration-300`}
                      style={{ width: passwordStrength.width }}
                    />
                  </div>
                </div>
              )}
              {errors.password && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <XCircle className="w-4 h-4 mr-1" />
                  {errors.password}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !usernameCheck.available || !emailValid.valid}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </button>

            {/* reCAPTCHA Badge */}
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mt-2 mb-1">
              <Shield className="w-3.5 h-3.5" />
              <span>Protected by reCAPTCHA</span>
            </div>

            {/* Trust Line */}
            <p className="text-center text-sm text-gray-500 flex items-center justify-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Your data is secure. No spam.
            </p>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-purple-600 hover:text-purple-700 font-semibold">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
